// Turns a filmmaker-submitted YouTube/Vimeo/Google Drive link into an
// embeddable player URL, so reviewers watch inline instead of following a
// raw link. Returns null for anything we don't recognize — callers should
// fall back to a plain link in that case (we can't safely iframe arbitrary
// sites; most block it via X-Frame-Options anyway).
export function getEmbedUrl(link) {
  if (!link) return null;
  let url;
  try { url = new URL(link); } catch { return null; }
  const host = url.hostname.replace(/^www\./, '');

  // fs=0 disables YouTube's own native fullscreen button — we provide our
  // own instead so fullscreen targets our wrapper (video + watermark), not
  // just the iframe. iv_load_policy=3 hides clickable annotation overlays.
  // Note: YouTube's "Watch on YouTube" channel link and end-screen video
  // suggestions cannot be removed via embed params — that's a platform
  // limitation, not something fixable here.
  const YT_PARAMS = 'rel=0&modestbranding=1&fs=0&iv_load_policy=3';

  if (host === 'youtu.be') {
    const id = url.pathname.slice(1).split('/')[0];
    if (id) return { type: 'youtube', embedUrl: `https://www.youtube-nocookie.com/embed/${id}?${YT_PARAMS}` };
  }

  if (host === 'youtube.com' || host === 'm.youtube.com') {
    if (url.pathname === '/watch') {
      const id = url.searchParams.get('v');
      if (id) return { type: 'youtube', embedUrl: `https://www.youtube-nocookie.com/embed/${id}?${YT_PARAMS}` };
    }
    const shortsMatch = url.pathname.match(/^\/shorts\/([^/]+)/);
    if (shortsMatch) return { type: 'youtube', embedUrl: `https://www.youtube-nocookie.com/embed/${shortsMatch[1]}?${YT_PARAMS}` };
    const embedMatch = url.pathname.match(/^\/embed\/([^/]+)/);
    if (embedMatch) return { type: 'youtube', embedUrl: `https://www.youtube-nocookie.com/embed/${embedMatch[1]}?${YT_PARAMS}` };
  }

  if (host === 'vimeo.com' || host === 'player.vimeo.com') {
    const match = url.pathname.match(/(\d+)(?:\/([a-zA-Z0-9]+))?\/?$/);
    if (match) {
      const [, id, hash] = match;
      return { type: 'vimeo', embedUrl: `https://player.vimeo.com/video/${id}${hash ? `?h=${hash}` : ''}` };
    }
  }

  if (host === 'drive.google.com') {
    const fileMatch = url.pathname.match(/\/file\/d\/([^/]+)/);
    if (fileMatch) return { type: 'drive', embedUrl: `https://drive.google.com/file/d/${fileMatch[1]}/preview` };
    const idParam = url.searchParams.get('id');
    if (idParam) return { type: 'drive', embedUrl: `https://drive.google.com/file/d/${idParam}/preview` };
  }

  return null;
}
